import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/app/Navbar";
import Footer from "@/components/app/Footer";
import Home from "@/pages/Home";
import AvailableNotes from "@/pages/AvailableNotes";
import NoteDetails from "@/pages/NoteDetails";
import HowItWorks from "@/pages/HowItWorks";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import UserDashboard from "@/pages/UserDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import Messages from "@/pages/Messages";
import Wallet from "@/pages/Wallet";
import Originations from "@/pages/Originations";
import OriginationDetails from "@/pages/OriginationDetails";
import BuyerApplication from "@/pages/BuyerApplication";
import DealerDashboard from "@/pages/DealerDashboard";
import EscrowSuccess from "@/pages/EscrowSuccess";
import InvestorApplications from "@/pages/InvestorApplications";
import InvestorApplicationDetails from "@/pages/InvestorApplicationDetails";
import DepositSuccess from "@/pages/DepositSuccess";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MagicLinkCallback from "@/pages/MagicLinkCallback";
import Legal from "@/pages/Legal";
import ComingSoon from "@/pages/ComingSoon";
import VerifyEmail from "@/pages/VerifyEmail";
import Profile from "@/pages/Profile";
import Notifications from "@/pages/Notifications";
import AdminImpersonationLogs from "@/pages/AdminImpersonationLogs";
import Portfolios from "@/pages/Portfolios";
import PortfolioDetails from "@/pages/PortfolioDetails";
import CreatePortfolio from "@/pages/CreatePortfolio";
import AdminPortfolioRequests from "@/pages/AdminPortfolioRequests";
import Deals from "@/pages/Deals";
import DealRoom from "@/pages/DealRoom";
import CreateListing from "@/pages/CreateListing";
import AdminDeals from "@/pages/AdminDeals";
import RequireAuth from "@/components/app/RequireAuth";
import RoleAwareDashboard from "@/components/app/RoleAwareDashboard";
import CookieBanner from "@/components/app/CookieBanner";
import EmailVerificationBanner from "@/components/app/EmailVerificationBanner";
import AdminToolbar from "@/components/app/AdminToolbar";
import { pageView, trackOutboundLinkClick } from "@/lib/analytics";

/**
 * Fire GA4 page_view + Clarity route tag on every SPA navigation. GA4's
 * config('G-...') auto-fires page_view only on the initial load; SPA route
 * changes must be reported manually. We also let Clarity know so its
 * heatmaps / recordings segment by URL correctly.
 */
function RouteAnalytics() {
  const location = useLocation();
  useEffect(() => {
    // Small tick so <useSEO> has updated document.title before we report it.
    const id = window.setTimeout(() => {
      pageView(location.pathname + location.search, document.title);
      try { if (typeof window.clarity === "function") window.clarity("set", "route", location.pathname); } catch { /* silent */ }
    }, 60);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.search]);
  return null;
}

/**
 * Track clicks on outbound (external) links anywhere in the app. Captured
 * once at the document root so pages don't have to opt in individually.
 */
function OutboundLinkTracker() {
  useEffect(() => {
    const host = window.location.hostname;
    const handler = (ev) => {
      const a = ev.target?.closest?.("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!/^https?:\/\//i.test(href)) return;
      try {
        const url = new URL(href);
        if (url.hostname === host) return;
        trackOutboundLinkClick(href, (a.textContent || "").trim().slice(0, 80));
      } catch { /* silent */ }
    };
    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true });
  }, []);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Detect session_id during render BEFORE protected route logic runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="min-h-screen flex flex-col">
      <RouteAnalytics />
      <OutboundLinkTracker />
      <AdminToolbar />
      <Navbar />
      <EmailVerificationBanner />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/notes" element={<AvailableNotes />} />
          <Route path="/notes/:id" element={<NoteDetails />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/coming-soon" element={<ComingSoon />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/originations" element={<Originations />} />
          <Route path="/originations/:id" element={<OriginationDetails />} />
          <Route path="/apply" element={<RequireAuth><BuyerApplication /></RequireAuth>} />
          <Route path="/buyer" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dealer" element={<RequireAuth role="dealer"><DealerDashboard /></RequireAuth>} />
          <Route path="/applications" element={<RequireAuth><InvestorApplications /></RequireAuth>} />
          <Route path="/applications/:id" element={<RequireAuth><InvestorApplicationDetails /></RequireAuth>} />
          <Route path="/deposit/success" element={<RequireAuth><DepositSuccess /></RequireAuth>} />
          <Route path="/escrow/success" element={<RequireAuth><EscrowSuccess /></RequireAuth>} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/magic" element={<MagicLinkCallback />} />
          <Route path="/dashboard" element={<RequireAuth><RoleAwareDashboard /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth role="admin"><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/impersonation-logs" element={<RequireAuth role="admin"><AdminImpersonationLogs /></RequireAuth>} />
          <Route path="/admin/portfolio-requests" element={<RequireAuth role="admin"><AdminPortfolioRequests /></RequireAuth>} />
          <Route path="/portfolios" element={<Portfolios />} />
          <Route path="/portfolios/new" element={<RequireAuth><CreatePortfolio /></RequireAuth>} />
          <Route path="/portfolios/:id" element={<PortfolioDetails />} />
          <Route path="/deals" element={<RequireAuth><Deals /></RequireAuth>} />
          <Route path="/deals/:id" element={<RequireAuth><DealRoom /></RequireAuth>} />
          <Route path="/sell/new" element={<RequireAuth><CreateListing /></RequireAuth>} />
          <Route path="/admin/deals" element={<RequireAuth role="admin"><AdminDeals /></RequireAuth>} />
          <Route path="/messages" element={<RequireAuth><Messages /></RequireAuth>} />
          <Route path="/wallet" element={<RequireAuth><Wallet /></RequireAuth>} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
      <Toaster richColors position="top-right" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
